/**
 * Message assembly for the staged debate: prepare, read the question, prepare
 * again, then answer.
 *
 * Free's sequence, per agent per question:
 *
 *   intro -> think -> question -> think -> answer
 *
 * THE ORDER IS THE WHOLE DESIGN, and it has a consequence worth stating plainly:
 * the last thing before the answer is the agent's OWN think block. That cannot be
 * expressed through `converse()`, which always appends the user turn last, so the
 * answer has to be generated as a CONTINUATION of the assistant's turn rather
 * than as a reply to a user turn.
 *
 * That is assistant prefill, and it is also the fix for the echo measured on
 * 2026-07-26. Deliberation was already being generated then, and the notes were
 * genuinely distinct, but they were being appended inside the USER message, so
 * the model read its own thought as something it had been told and never owned
 * it. Prefilled into its own turn, the note is something it already said, and
 * continuing is the natural thing to do.
 *
 * The other load-bearing property: in the opening round an agent sees the
 * QUESTION and not the opponent's answer. A speaker with nothing to echo cannot
 * echo. Rebuttal is where the opponent belongs, and this file keeps the two
 * cases separate so that cannot be blurred by accident.
 */

import { buildContext, flattenForModel, HANDOFF, REST_TOKEN } from "./debateContext.ts";
import {
  prompt as renderPrompt,
  modePrompt,
  type PromptOverrides,
  type DebateMode,
} from "./promptKit.ts";
import type { Beat } from "./debateContext.ts";
import type { BudgetedMsg } from "./contextBudget.ts";

export type Role = "system" | "user" | "assistant";
export type Msg = { role: Role; content: string };

export type RoundSpeaker = { persona: string };

/**
 * `HANDOFF` and `REST_TOKEN` moved down into `debateContext`, where the
 * flattener needs them, and are re-exported here so callers do not care which
 * layer owns them.
 */
export { HANDOFF, REST_TOKEN } from "./debateContext.ts";

/**
 * Once per debate: who you are up against, and about what.
 *
 * Asks for STANCE, not facts, and says so twice. The staging pass runs without
 * retrieval (measured: it produces two distinct positions instead of two
 * lyrical ones), and a pass with nothing to check against will happily invent
 * specifics. It produced "roughly 67% of the arable land experiences optimal
 * conditions" from nowhere at all. Figures belong to the per-question think,
 * which does retrieve, and where an invented number can be contradicted.
 */
export function introPrompt(
  other: string,
  topic: string,
  goesFirst: boolean,
  ov?: PromptOverrides,
  mode: DebateMode = "debate",
): string {
  return modePrompt(mode, "intro", { other, topic, order: goesFirst ? "first" : "second" }, ov);
}

/** Once per question: the question, and one more chance to prepare for it. */
export function questionPrompt(question: string, index: number, ov?: PromptOverrides): string {
  return renderPrompt("question", { question, n: String(index + 1) }, ov);
}

/**
 * The messages that produce an OPENING statement.
 *
 * Ends on an assistant message on purpose: the caller prefills it and the model
 * continues, so the answer flows out of the agent's own preparation instead of
 * replying to an instruction.
 *
 * `facts` is the memory banner, placed with the question rather than at the top,
 * because the retrieved facts are about THIS question and should sit next to it.
 */
export function openingContext(args: {
  intro: string;
  stagingNote: string;
  question: string;
  questionIndex: number;
  questionNote: string;
  facts?: string;
  mode?: DebateMode;
}): BudgetedMsg[] {
  const { intro, stagingNote, question, questionIndex, questionNote, facts, mode } = args;
  void questionIndex;
  return buildContext({
    intro,
    stagingNote,
    question,
    instruction: mode ? openingInstruction(undefined, mode) : OPENING_INSTRUCTION,
    facts,
    // The prefill. `flattenForModel` appends the hand-off, so the turn ends
    // mid-sentence and the model continues its own thought rather than
    // replying to it.
    liveNote: questionNote,
  });
}

export function openingMessages(args: Parameters<typeof openingContext>[0]): Msg[] {
  return flattenForModel(openingContext(args));
}

/**
 * "Nobody has spoken yet" is load-bearing, not decoration.
 *
 * Without it the second speaker opened with "That's a compelling observation!",
 * reacting enthusiastically to a phantom, because a prefilled continuation reads
 * as mid-conversation even when the conversation has not started. An opening
 * statement that answers someone is wrong in a way that is easy to miss and it
 * undermines the whole point of the round being independent.
 */
export const openingInstruction = (ov?: PromptOverrides, mode: DebateMode = "debate") =>
  modePrompt(mode, "opening", {}, ov);

/** The unedited text, for the callers that never take an override. */
const OPENING_INSTRUCTION = openingInstruction();

/**
 * Prepare a rebuttal: read what they said, decide where they are wrong.
 *
 * Their answer is quoted here so the agent can react to it, but the RETRIEVAL
 * that follows is driven by the resulting note plus the question, never by their
 * raw text. Searching one's own lattice with the opponent's words is what
 * produced the paraphrasing in the first place.
 */
export function rebuttalThinkPrompt(
  otherPersona: string,
  question: string,
  theirAnswer: string,
  ov?: PromptOverrides,
  mode: DebateMode = "debate",
): string {
  return modePrompt(mode, "rebuttalThink", { other: otherPersona, question, theirAnswer }, ov);
}

/**
 * The messages that produce a REBUTTAL.
 *
 * This is the only place the opponent's words appear, which is what keeps the
 * opening round independent. Their answer arrives as a user turn, then this
 * agent's rebuttal note is prefilled so the reply continues from its own
 * thinking rather than from their paragraph.
 */
export function rebuttalMessages(args: {
  intro: string;
  stagingNote: string;
  question: string;
  ownAnswer: string;
  otherPersona: string;
  otherAnswer: string;
  rebuttalNote: string;
  facts?: string;
  /**
   * Everything already said on this question, oldest first, EXCLUDING the two
   * turns named above. Left empty this behaves exactly as before: a rolling
   * window of one statement each. Supplied, the agent can see the arc of its own
   * argument instead of meeting each exchange fresh.
   */
  history?: Beat[];
  mode?: DebateMode;
}): Msg[] {
  const {
    intro, stagingNote, question, ownAnswer, otherPersona, otherAnswer, rebuttalNote, facts,
    history = [], mode,
  } = args;
  return flattenForModel(
    buildContext({
      intro,
      stagingNote,
      question,
      beats: [
        ...history,
        { who: "self", text: ownAnswer },
        { who: "other", text: quoteOther(otherPersona, otherAnswer) },
      ],
      instruction: mode ? rebuttalInstruction(undefined, mode) : REBUTTAL_INSTRUCTION,
      facts,
      liveNote: rebuttalNote,
    }),
  );
}

/**
 * Attribute a turn, so whose words these are is never in doubt.
 *
 * NO QUOTATION MARKS, and that is a fix rather than a style choice. Handing the
 * opponent's answer over inside quotes taught the model that a reply looks like
 * a quoted paragraph: 6 of 8 turns in the failing run came back wrapped in
 * curly quotes. Those got stored, quoted again on the way back in, and by the
 * third exchange there was no marker left that the model could trust. The
 * measured end state was one speaker reproducing the other's attack ON ITSELF,
 * referring to itself in the third person.
 *
 * The chat template already separates the turns by role. The quotes added
 * nothing but a pattern to imitate.
 */
export function quoteOther(persona: string, text: string): string {
  return `${persona} said:\n\n${text}`;
}

/**
 * The yield token is rendered INTO this prompt, so overriding one and not the
 * other would tell the model to say a word that `hasYielded` does not accept.
 * Both come from the registry, so they cannot drift apart.
 */
export const rebuttalInstruction = (ov?: PromptOverrides, mode: DebateMode = "debate") =>
  modePrompt(mode, "rebuttal", { rest: renderPrompt("rest", {}, ov) }, ov);

/** The unedited text, for the callers that never take an override. */
export const REBUTTAL_INSTRUCTION = rebuttalInstruction();

/**
 * Has this agent yielded?
 *
 * Deliberately strict about length. A model that says "rest" and then keeps
 * talking has not yielded, it has merely used the word, and treating that as a
 * yield would end debates early for the wrong reason.
 */
export function hasYielded(reply: string): boolean {
  const t = reply
    .trim()
    .toLowerCase()
    .replace(/^["“']|["”'.!]+$/g, "")
    .trim();
  if (t === REST_TOKEN) return true;
  // Allow a very short sentence that is unambiguously a yield.
  if (t.length <= 40 && /\b(i )?(rest|have stated my case|have nothing (further|to add))\b/.test(t)) {
    return true;
  }
  return false;
}

/** Strip a prefill from the front of a continuation, so it is not stored twice. */
export function stripPrefill(prefill: string, continuation: string): string {
  let c = continuation.trim();
  const p = prefill.trim();
  if (p && c.startsWith(p)) c = c.slice(p.length).trim();
  // The hand-off may be echoed back whether or not the note was.
  if (c.startsWith(HANDOFF)) c = c.slice(HANDOFF.length).trim();
  // Continuing a deliberately unfinished sentence sometimes produces a leading
  // ellipsis or connective, which reads as a fragment once the prefill is gone.
  c = c.replace(/^(…|\.\.\.|,)\s*/, "").trim();
  return c;
}

/**
 * Does this read as a reply to someone rather than as an opening statement?
 *
 * TWO instrument failures lived in this one function, and both made it report
 * "clean" on output that plainly was not.
 *
 * It first used a straight apostrophe only, and models write a curly one, so
 * every "That's ..." opener slipped past unseen.
 *
 * Then a word-boundary marker, authored through a shell here-document into a
 * script, collapsed into a literal BACKSPACE character (0x08). That byte is
 * invisible in every listing, and JSON.stringify renders it exactly like the
 * two-character escape it was meant to be, so the regex looked right while
 * matching nothing at all. Four rounds of diagnosis went past it before dumping
 * raw bytes. Nothing here needs a boundary marker anyway: an anchored
 * alternation does not use one.
 *
 * The transferable lesson: do not author regexes through layered here-documents.
 */
const REACTIVE =
  /^(that['’]?s|what an|great|interesting|good point|indeed|absolutely|i agree|you (make|raise)|excellent)/i;

export function opensReactively(answer: string): boolean {
  return REACTIVE.test(answer.trim());
}
